import React, { useEffect, useState } from 'react';
import { XIcon, AlertTriangleIcon } from 'lucide-react';
import { useAuth } from './auth/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { deleteUser } from 'firebase/auth';
import { auth } from '../firebase/firebaseConfig';
import { deleteUser as deleteFirestoreUser } from '../firebase/auth';
interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose
}) => {
  const {
    logout,
    user
  } = useAuth();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setConfirmText('');
      setError('');
      setIsDeleting(false);
    }
  }, [isOpen]);
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save the current overflow style
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const originalPaddingRight = window.getComputedStyle(document.body).paddingRight;
      // Calculate scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      // Prevent scrolling on the body
      document.body.style.overflow = 'hidden';
      // Add padding right to prevent layout shift when scrollbar disappears
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      // Restore the original style when modal closes
      return () => {
        document.body.style.overflow = originalStyle;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const handleDeleteAccount = async () => {
    // Confirm that user typed "DELETE" correctly
    if (confirmText !== 'DELETE') {
      setError('Please type DELETE to confirm account deletion');
      return;
    }
    setIsDeleting(true);
    setError('');
    try {
      if (user && user.id) {
        // Delete user from Firestore
        await deleteFirestoreUser(user.id);
        // Delete user from Firebase Authentication
        const currentUser = auth.currentUser;
        if (currentUser) {
          await deleteUser(currentUser);
        }
        // Log out the user
        await logout();
        // Show toast notification
        alert('Your account has been deleted successfully.');
        // Close modal
        onClose();
        // Redirect to landing page
        navigate('/');
      } else {
        throw new Error('User not found');
      }
    } catch (err) {
      console.error('Error deleting account:', err);
      setError('An error occurred while deleting your account. Please try again.');
      setIsDeleting(false);
    }
  };
  // Stop propagation to prevent clicks inside the modal from closing it
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4" onClick={onClose} data-id="element-1061">
      <div className="bg-white dark:bg-[#162825] rounded-2xl shadow-xl w-full max-w-md relative overflow-hidden" onClick={handleModalClick} data-id="element-1062">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-10" data-id="element-1063">
          <XIcon className="h-5 w-5" data-id="element-1064" />
        </button>
        <div className="p-8" data-id="element-1065">
          <div className="flex items-center justify-center mb-4" data-id="element-1066">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center text-red-500 dark:text-red-400" data-id="element-1067">
              <AlertTriangleIcon className="h-6 w-6" data-id="element-1068" />
            </div>
          </div>
          <div className="text-center mb-6" data-id="element-1069">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-1070">
              Delete Your Account?
            </h2>
            <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-1071">
              This action is permanent and cannot be undone. All your data,
              including both buyer and seller profiles, will be permanently
              deleted.
            </p>
          </div>
          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm" data-id="element-1072">
              {error}
            </div>}
          <div className="mb-6" data-id="element-1073">
            <label htmlFor="confirm-delete" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-1074">
              Type DELETE to confirm
            </label>
            <input id="confirm-delete" type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)} className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-[#1A3330] focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]" placeholder="DELETE" data-id="element-1075" />
          </div>
          <div className="flex space-x-4" data-id="element-1076">
            <button onClick={onClose} className="flex-1 py-2 px-4 bg-gray-100 dark:bg-[#1A3330] text-gray-700 dark:text-[#B0C1BD] rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-[#214541] transition-colors" data-id="element-1077">
              Cancel
            </button>
            <button onClick={handleDeleteAccount} disabled={isDeleting} className="flex-1 py-2 px-4 bg-red-500 dark:bg-red-600 text-white rounded-lg font-medium hover:bg-red-600 dark:hover:bg-red-700 transition-colors disabled:opacity-70" data-id="element-1078">
              {isDeleting ? <span className="flex items-center justify-center" data-id="element-1079">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1080">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1081"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1082"></path>
                  </svg>
                  Deleting...
                </span> : 'Delete Account'}
            </button>
          </div>
        </div>
      </div>
    </div>;
};